/**
 * HowToPlayModal — one-shot first-match tutorial.
 *
 * Fires the first time a player lands on /play in a given browser
 * (gated by a localStorage flag). Answers the questions a first-time
 * player actually asks: "which territory do I click? why? what are
 * the numbers? what does committing cubes do?"
 *
 * Also listens for a `qd:show-howto` window event so the in-game Help
 * button can re-open the tutorial without forcing the user to know
 * about localStorage.
 *
 * Reset for testing: `localStorage.removeItem('qd-tutorial-seen-v2')`.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { fontStacks, surfaces } from '@qd/design-tokens';
import { ChalkButton } from '../base/ChalkButton';
import { ChalkText } from '../base/ChalkText';

// v2 — bumped because v1 keys from earlier test sessions persisted in
// browser localStorage and locked players out of the tutorial forever.
const STORAGE_KEY = 'qd-tutorial-seen-v2';

/** Window event other components dispatch to re-open the tutorial. */
export const HOW_TO_PLAY_EVENT = 'qd:show-howto';

export function HowToPlayModal(): ReactNode {
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setOpen(true);
    window.addEventListener(HOW_TO_PLAY_EVENT, handler);
    return () => {
      window.removeEventListener(HOW_TO_PLAY_EVENT, handler);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      if (dontShowAgain) {
        window.localStorage.setItem(STORAGE_KEY, '1');
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // ignore
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="qd-howtoplay-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(50, 42, 30, 0.55)',
        backdropFilter: 'blur(2px)',
        padding: 24,
        overflowY: 'auto',
      }}
    >
      <div
        style={{
          maxWidth: 620,
          width: '100%',
          background: surfaces.slate,
          border: `1px solid ${surfaces.chalk}`,
          borderRadius: 4,
          padding: '32px 36px',
          color: surfaces.chalk,
          fontFamily: fontStacks.body,
          boxShadow: '0 18px 50px rgba(0,0,0,0.5)',
        }}
      >
        <div style={{ marginBottom: 22 }}>
          <ChalkText size="heading" underline>
            <span id="qd-howtoplay-title">How to play</span>
          </ChalkText>
        </div>

        <Section title="The board">
          <p style={pStyle}>
            Every coloured region is a <strong>territory</strong>. The colour shows
            who controls it — your colour appears at the top of the screen. The
            small number on each territory is its <strong>cube count</strong> —
            think of cubes as your forces. More cubes = stronger territory.
          </p>
        </Section>

        <Section title="Your turn — attack flow">
          <ol style={olStyle}>
            <Step n={1}>
              <strong>Click one of YOUR territories</strong> with at least 2 cubes.
              That's the territory you'll <em>attack from</em>. It will get a
              red outline.
            </Step>
            <Step n={2}>
              <strong>Click an adjacent ENEMY territory.</strong> The reachable
              enemies pulse with a white halo — those are your only valid targets.
              Clicking one of them sets it as the territory you'll <em>attack into</em>.
            </Step>
            <Step n={3}>
              <strong>Commit cubes.</strong> Pick 1, 2, or 3 cubes (you must keep at
              least 1 home for defense). Each committed cube buys you one quiz
              attempt. More cubes = more chances to win the territory.
            </Step>
          </ol>
        </Section>

        <Section title="Combat — the quiz">
          <ul style={ulStyle}>
            <li style={liStyle}>
              <strong>Right answer</strong> → it's a <em>hit</em>. The defender
              loses one cube.
            </li>
            <li style={liStyle}>
              <strong>Wrong answer</strong> → it's a <em>miss</em>. <em>You</em>
              {' '}lose one cube.
            </li>
            <li style={liStyle}>
              Combat continues until either side runs out of cubes, or you choose
              to stop. Drop the defender to 0 cubes and the territory is yours.
            </li>
          </ul>
        </Section>

        <Section title="Win condition">
          <p style={pStyle}>
            Capture <strong>every territory</strong> on the map. The last colour
            standing wins.
          </p>
        </Section>

        <div
          style={{
            marginTop: 26,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
              fontFamily: fontStacks.mono,
              fontSize: 10,
              letterSpacing: '0.16em',
              color: surfaces.chalk,
              opacity: dontShowAgain ? 0.9 : 0.5,
              userSelect: 'none',
            }}
          >
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              style={{ cursor: 'pointer', accentColor: surfaces.waxRed }}
            />
            WON'T SHOW AGAIN ON THIS DEVICE
          </label>
          <ChalkButton variant="primary" onClick={dismiss} ariaLabel="Got it, start playing">
            Got it
          </ChalkButton>
        </div>
      </div>
    </div>
  );
}

const pStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.5,
  color: surfaces.chalk,
  opacity: 0.92,
};

const olStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const ulStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  fontSize: 14,
  lineHeight: 1.5,
};

const liStyle: React.CSSProperties = {
  paddingLeft: 14,
  borderLeft: `2px solid ${surfaces.graphite}`,
};

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section style={{ marginBottom: 18 }}>
      <h3
        style={{
          margin: '0 0 8px',
          fontFamily: fontStacks.display,
          fontSize: 12,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: surfaces.chalk,
          opacity: 0.7,
          fontWeight: 700,
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function Step({ n, children }: { n: number; children: ReactNode }): ReactNode {
  return (
    <li style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 14, lineHeight: 1.5 }}>
      <span
        aria-hidden
        style={{
          flex: '0 0 auto',
          width: 26,
          height: 26,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          border: `1px solid ${surfaces.chalk}`,
          fontFamily: fontStacks.display,
          fontSize: 13,
          fontWeight: 700,
          color: surfaces.chalk,
          marginTop: 2,
        }}
      >
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}
